import { APIClient } from "../helpers/api_helper";
import { ApiResponse, PaginatedApiResponse, PaginationParams } from "../types/api";

const api = new APIClient();
const RESOURCE = "/support/conversations";

export type SupportConversationStatus =
  | "open_ai"
  | "queued_human"
  | "assigned"
  | "closed";

export interface SupportConversationHelpLink {
  title: string;
  url: string;
}

export interface SupportConversationMessage {
  id: number;
  author_type: "user" | "ai" | "agent" | "system";
  body: string;
  help_links: SupportConversationHelpLink[];
  meta?: {
    kind?: string;
    actions?: string[];
  } | null;
  created_at: string | null;
}

export interface SupportConversation {
  id: number;
  tenant_id?: number;
  user_id?: number | null;
  channel: "app" | "whatsapp";
  locale: string;
  status: SupportConversationStatus;
  ai_turn_count: number;
  ai_max_turns: number;
  assigned_super_admin_id?: number | null;
  queued_human_at: string | null;
  closed_at: string | null;
  closed_reason: string | null;
  support_ticket_id: number | null;
  messages_count?: number;
  messages?: SupportConversationMessage[];
  latest_user_message?: {
    id: number;
    body: string;
    created_at: string | null;
  } | null;
  tenant?: { id: number; name: string } | null;
  user?: { id: number; name: string; email: string } | null;
  assigned_super_admin?: { id: number; name: string } | null;
  created_at: string | null;
  updated_at: string | null;
}

export const listSupportConversations = (params?: PaginationParams) =>
  api.get(RESOURCE, params) as Promise<PaginatedApiResponse<SupportConversation>>;

export const createSupportConversation = (locale?: string) =>
  api.create(RESOURCE, locale ? { locale } : {}) as Promise<
    ApiResponse<SupportConversation>
  >;

export const getSupportConversation = (id: number) =>
  api.get(`${RESOURCE}/${id}`) as Promise<ApiResponse<SupportConversation>>;

export const postSupportConversationMessage = (id: number, body: string) =>
  api.create(`${RESOURCE}/${id}/messages`, { body }) as Promise<
    ApiResponse<{
      message: SupportConversationMessage;
      conversation: SupportConversation;
    }>
  >;

export const closeSupportConversation = (id: number) =>
  api.create(`${RESOURCE}/${id}/close`, {}) as Promise<
    ApiResponse<SupportConversation>
  >;

export const requestSupportHuman = (id: number) =>
  api.create(`${RESOURCE}/${id}/request-human`, {}) as Promise<
    ApiResponse<SupportConversation>
  >;

export type SupportConversationTicketCategory =
  | "agenda"
  | "orders"
  | "payments"
  | "account"
  | "integration"
  | "other";

export const openSupportConversationTicket = (
  id: number,
  category: SupportConversationTicketCategory
) =>
  api.create(`${RESOURCE}/${id}/open-ticket`, { category }) as Promise<
    ApiResponse<{
      conversation: SupportConversation;
      ticket: {
        id: number;
        status: string;
        subject: string;
        category?: string;
      };
    }>
  >;

export const respondSupportConversationSla = (
  id: number,
  action: "accept_ticket" | "continue_waiting",
  category?: SupportConversationTicketCategory
) =>
  api.create(`${RESOURCE}/${id}/sla-response`, {
    action,
    ...(category ? { category } : {}),
  }) as Promise<ApiResponse<SupportConversation>>;
